import { AfterViewInit, Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CustomValidators } from 'src/app/shared/classes/CustomValidators';
import { Helper } from 'src/app/shared/classes/Helper';
import { HitApi } from 'src/app/shared/classes/HitApi';
import { Widget } from 'src/app/shared/classes/Widget';
import { WidgetInjectedData } from 'src/app/shared/classes/WidgetInjectedData';
import { ButtonColorType } from 'src/app/shared/enums/ButtonColorType';
import { ButtonType } from 'src/app/shared/enums/ButtonType';
import { FilterType } from 'src/app/shared/enums/FilterType';
import { FormState } from 'src/app/shared/enums/FormState';
import { IconType } from 'src/app/shared/enums/IconType';
import { ModalType } from 'src/app/shared/enums/ModalType';
import { IIntegrationApiResponse } from 'src/app/shared/interfaces/api/portlets/IIntegrationApiResponse';
import { IButtonGeneratorInput } from 'src/app/shared/interfaces/button-generator/IButtonGeneratorInput';
import { IFormField } from 'src/app/shared/interfaces/form-generator/IFormField';
import { IFormGeneratorInput } from 'src/app/shared/interfaces/form-generator/IFormGeneratorInput';
import { IIntegrationInput } from 'src/app/shared/interfaces/integration/IIntegrationInput';
import { IModalData } from 'src/app/shared/interfaces/modal/IModalData';
import { IframeOpenerModalComponent } from '../../../modal-templates/iframe-opener-modal/iframe-opener-modal.component';
import { FormGeneratorModalComponent } from './../../../modal-templates/form-generator-modal/form-generator-modal.component';

@Component({
    selector: 'app-freshdesk-integration',
    templateUrl: './freshdesk-integration.component.html',
    styleUrls: ['./freshdesk-integration.component.sass']
})
export class FreshdeskIntegrationComponent implements OnInit, AfterViewInit {
    integrationInput: IIntegrationInput = null;
    widgetRef: Widget;
    isParentIntegrated = false;
    isSelfIntegrated = false;
    isConfigured: Boolean = false;
    formGenInput: IFormGeneratorInput;

    constructor(widgetData: WidgetInjectedData) {
        this.widgetRef = widgetData.widgetRef;
    }

    ngOnInit(): void {
        this.setUpBasics();
    }

    setUpBasics() {
        this.integrationInput = {
            integrationName: this.widgetRef.widgetData.widgetInfo.text,
            integrationCategory: 'ITSM Integration',
            imageUrl: 'assets/integrations/freshdesk_2.png',
            buttons: [],
            imagePadding: 6
        };
    }

    generateButtons(response: IIntegrationApiResponse) {
        this.formGenInput = {
            formName: 'Freshdesk',
            state: this.isSelfIntegrated ? FormState.EDIT : FormState.CREATE,
            submitButton: {
                buttonName: this.isSelfIntegrated ? 'Update' : 'Integrate',
                buttonType: ButtonType.FLAT,
                buttonColorType: ButtonColorType.PRIMARY,
                showLoader: true,
                function: (
                    buttonRef: IButtonGeneratorInput,
                    formGroup: FormGroup,
                    modalId: Symbol
                ) => {
                    if (formGroup.valid) {
                        buttonRef.loader = true;
                        if (this.isSelfIntegrated) {
                            // Update
                            const args = Helper.generateHitApiConfig(
                                this.widgetRef.widgetData.widgetInfo.update
                            );
                            args.input = {
                                ...formGroup.value,
                                integrationType: 'FRESHDESK'
                            };
                            args.function = (response) => {
                                this.widgetRef.notificationsService.showSnackBar(
                                    'Freshdesk configuration updated successfully'
                                );
                                buttonRef.loader = false;
                                this.widgetRef.refreshWidget();
                                this.widgetRef.modalService.closeModal(
                                    null,
                                    modalId
                                );
                            };
                            args.errorFunction = (error) => {
                                this.widgetRef.notificationsService.showSnackBar(
                                    'Error updating freshdesk configuration'
                                );
                                buttonRef.loader = false;
                            };
                            new HitApi(
                                args,
                                this.widgetRef.httpService,
                                this.widgetRef.ngZone
                            ).hitApi();
                        } else {
                            // Create
                            const args = Helper.generateHitApiConfig(
                                this.widgetRef.widgetData.widgetInfo.create
                            );
                            args.input = {
                                ...formGroup.value,
                                integrationType: 'FRESHDESK'
                            };
                            args.function = (response) => {
                                this.widgetRef.notificationsService.showSnackBar(
                                    'Freshdesk integrated successfully'
                                );
                                buttonRef.loader = false;
                                this.widgetRef.refreshWidget();
                                this.widgetRef.modalService.closeModal(
                                    null,
                                    modalId
                                );
                            };
                            args.errorFunction = (error) => {
                                this.widgetRef.notificationsService.showSnackBar(
                                    'Error integrating freshdesk'
                                );
                                buttonRef.loader = false;
                            };
                            new HitApi(
                                args,
                                this.widgetRef.httpService,
                                this.widgetRef.ngZone
                            ).hitApi();
                        }
                    }
                }
            },
            fields: [
                {
                    name: 'email',
                    placeholder: 'Email',
                    label: 'Email',
                    fieldType: FilterType.EMAIL,
                    required: true,
                    showLabel: true,
                    appearance: 'legacy',
                    validations: [
                        {
                            validator: CustomValidators.required,
                            errorMessage: 'Email is required'
                        },
                        {
                            validator: CustomValidators.email,
                            errorMessage: 'Invalid email'
                        }
                    ],
                    value: this.isSelfIntegrated
                        ? response['self']['email']
                        : ''
                },
                {
                    name: 'supportedEmails',
                    placeholder: 'Support Email',
                    label: 'Support Email',
                    fieldType: FilterType.MATCHIPLIST,
                    required: true,
                    showLabel: true,
                    appearance: 'legacy',
                    validations: [
                        {
                            validator: CustomValidators.required,
                            errorMessage: 'Support email is required'
                        },
                        {
                            validator: CustomValidators.matChipAllowEmail,
                            errorMessage: 'Invalid email'
                        }
                    ],
                    value: this.isSelfIntegrated
                        ? response['self']['supportedEmails']
                        : ''
                },
                {
                    name: 'apiKey',
                    placeholder: 'API Key',
                    label: 'API Key',
                    fieldType: FilterType.TEXT,
                    required: true,
                    showLabel: true,
                    appearance: 'legacy',
                    suffixIcon: {
                        hoverText: `Where to get API Key`,
                        iconData: {
                            type: IconType.MATICON,
                            class: 'help'
                        },
                        function: (field: IFormField, formGroup: FormGroup) => {
                            this.widgetRef.modalService.openInfoModal({
                                infoHeading: 'Your API Key',
                                content: [
                                    {
                                        type: 'ORDERED_LIST',
                                        data: [
                                            `Login to your Freshdesk Account.`,
                                            `Click on your profile picture icon on the top right corner and select Profile Settings.`,
                                            `On the right pane, you will find the API Key.`,
                                            `Copy-paste this key in the API key field.`
                                        ]
                                    }
                                ]
                            });
                        }
                    },
                    validations: [
                        {
                            validator: CustomValidators.required,
                            errorMessage: 'API key is required'
                        }
                    ],
                    value: this.isSelfIntegrated
                        ? response['self']['apiKey']
                        : ''
                },
                {
                    name: 'domainName',
                    placeholder: 'Domain Name',
                    label: 'Domain Name',
                    fieldType: FilterType.TEXT,
                    required: true,
                    showLabel: true,
                    appearance: 'legacy',
                    validations: [
                        {
                            validator: CustomValidators.required,
                            errorMessage: 'Domain name is required'
                        }
                    ],
                    value: this.isSelfIntegrated
                        ? response['self']['domainName']
                        : ''
                },
                {
                    name: 'widgetCode',
                    placeholder: 'Widget Code',
                    label: 'Widget Code',
                    fieldType: FilterType.TEXTAREA,
                    required: true,
                    showLabel: true,
                    appearance: 'legacy',
                    extraClass: 'tw-items-center',
                    suffixIcon: {
                        hoverText: `Where to get widget code`,
                        iconData: {
                            type: IconType.MATICON,
                            class: 'help'
                        },
                        function: (field: IFormField, formGroup: FormGroup) => {
                            this.widgetRef.modalService.openInfoModal({
                                infoHeading: 'Your Widget Code',
                                content: [
                                    {
                                        type: 'ORDERED_LIST',
                                        data: [
                                            `Log into your support portal as an administrator.`,
                                            `Click on Admin > Support Channels > Feedback Widget.`
                                        ]
                                    }
                                ]
                            });
                        }
                    },
                    validations: [
                        {
                            validator: CustomValidators.required,
                            errorMessage: 'Widget code is required'
                        }
                    ],
                    value: this.isSelfIntegrated
                        ? response['self']['widgetCode']
                        : '',
                    fieldStyle: {
                        height: '40%'
                    },
                    textAreaStyles: {
                        'min-height': '80px'
                    }
                }
            ],
            extraClass: 'integration-new-styling add-scroll'
        };
        if (this.isSelfIntegrated || this.isParentIntegrated) {
            if (this.isSelfIntegrated) {
                // Adding play button
                this.integrationInput.buttons.push({
                    buttonName: 'Test',
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-play'
                    },
                    buttonType: ButtonType.CIRCLE,
                    buttonColorType: ButtonColorType.SUCCESS,
                    hoverText: 'Test Freshdesk',
                    function: () => {
                        const modalArgs: IModalData = {
                            modalName: 'Create Ticket',
                            sourceId:
                                this.widgetRef.widgetData
                                    .widgetUniqueIdentifier,
                            modalType: ModalType.MIDDLE,
                            modalIcon: {
                                type: IconType.FONTAWSOME,
                                class: 'fas fa-play'
                            },
                            modalSteps: [
                                {
                                    stepName: 'Create Ticket',
                                    stepData: {
                                        componentToLoad:
                                            IframeOpenerModalComponent,
                                        payload: {
                                            data: this.isSelfIntegrated
                                                ? response.self.widgetCode
                                                : this.isParentIntegrated
                                                ? response.parent.widgetCode
                                                : null
                                        }
                                    }
                                }
                            ],
                            modalWidthVw: 60,
                            modalHeightVh: 60,
                            noBottomPadding: true
                        };
                        this.widgetRef.modalService.openModal(modalArgs);
                    }
                });

                // Adding edit button
                this.integrationInput.buttons.push({
                    buttonName: 'Edit',
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-wrench'
                    },
                    buttonType: ButtonType.CIRCLE,
                    buttonColorType: ButtonColorType.SUCCESS,
                    hoverText: 'Edit Freshdesk',
                    function: () => {
                        const modalArgs: IModalData = {
                            modalName: 'Edit Freshdesk',
                            sourceId:
                                this.widgetRef.widgetData
                                    .widgetUniqueIdentifier,
                            modalType: ModalType.MIDDLE,
                            modalIcon: {
                                type: IconType.FONTAWSOME,
                                class: 'fas fa-wrench'
                            },
                            extraStepContainerClass: 'remove-form-space',
                            modalSteps: [
                                {
                                    stepName: 'Edit Freshdesk',
                                    stepData: {
                                        componentToLoad:
                                            FormGeneratorModalComponent,
                                        payload: {
                                            data: this.formGenInput
                                        }
                                    }
                                }
                            ],
                            modalWidthVw: 60,
                            modalHeightVh: 70,
                            noBottomPadding: true
                        };
                        this.widgetRef.modalService.openModal(modalArgs);
                    }
                });

                // Adding delete button
                this.integrationInput.buttons.push({
                    buttonName: 'Delete',
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-trash'
                    },
                    hoverText: 'Remove Freshdesk',
                    buttonType: ButtonType.CIRCLE,
                    buttonColorType: ButtonColorType.WARN,
                    function: (modalId: Symbol) => {
                        // Delete integration
                        const args = Helper.generateHitApiConfig(
                            this.widgetRef.widgetData.widgetInfo.delete
                        );
                        args.input = {};
                        args.function = (response) => {
                            this.widgetRef.notificationsService.showSnackBar(
                                `Freshdesk integration removed successfully`
                            );
                            this.widgetRef.modalService.closeModal(
                                null,
                                modalId
                            );
                            this.widgetRef.refreshWidget();
                        };
                        args.errorFunction = (error) => {
                            this.widgetRef.notificationsService.showSnackBar(
                                error.error.message,
                                true
                            );
                            this.widgetRef.modalService.closeModal(
                                null,
                                modalId
                            );
                        };
                        new HitApi(
                            args,
                            this.widgetRef.httpService,
                            this.widgetRef.ngZone
                        ).hitApi();
                    },
                    executeFunctionAfterConfirmation: {
                        modalName: 'Remove Freshdesk Integration',
                        modalIcon: {
                            type: IconType.FONTAWSOME,
                            class: 'far fa-trash-alt'
                        },
                        contextIcon: {
                            extraClass: 'color-accent',
                            type: IconType.FONTAWSOME,
                            class: 'fas fa-exclamation-triangle'
                        },
                        confirmationMessage: `Are you sure you want to remove freshdesk ?`,
                        buttonText: 'Remove',
                        buttonColorType: ButtonColorType.WARN,
                        loaderOnExec: true
                    }
                });
            }

            if (this.isParentIntegrated && !this.isSelfIntegrated) {
                // Adding create button
                this.integrationInput.buttons.push({
                    buttonName: 'Integrate',
                    buttonIcon: {
                        type: IconType.FONTAWSOME,
                        class: 'fas fa-plus'
                    },
                    buttonType: ButtonType.CIRCLE,
                    buttonColorType: ButtonColorType.SUCCESS,
                    hoverText: 'Integrate Freshdesk',
                    function: () => {
                        this.onIntegrate(this.formGenInput);
                    }
                });
            }
        }
    }

    onIntegrate(formGenInput) {
        const modalArgs: IModalData = {
            modalName: 'Integrate Freshdesk',
            sourceId: this.widgetRef.widgetData.widgetUniqueIdentifier,
            modalType: ModalType.MIDDLE,
            modalIcon: {
                type: IconType.FONTAWSOME,
                class: 'fas fa-ticket-alt'
            },
            extraStepContainerClass: 'remove-form-space remove-bottom-padding',
            modalSteps: [
                {
                    stepName: 'Integrate Freshdesk',
                    resetModal: true,
                    stepData: {
                        componentToLoad: FormGeneratorModalComponent,
                        payload: {
                            data: formGenInput
                        }
                    }
                }
            ],
            modalWidthVw: 60,
            modalHeightPx: 470
        };
        this.widgetRef.modalService.openModal(modalArgs);
    }
    bindData(response: IIntegrationApiResponse): void {
        this.isConfigured = false;
        if ('integration' in response && !response.integration) {
            this.isParentIntegrated = false;
            this.isSelfIntegrated = false;
        }
        if (response.parent && response.parent.integration) {
            this.isParentIntegrated = true;
            this.isSelfIntegrated = false;
        }
        if (response.self && response.self.integration) {
            this.isSelfIntegrated = true;
            this.isConfigured = true;
        }
        this.integrationInput.buttons = [];
        this.generateButtons(response);
        this.widgetRef.endLoader();
    }

    ngAfterViewInit(): void {
        this.widgetRef.setBindData(this.bindData.bind(this));
    }
}
